/**
 * MQL5PromptsTab Component
 * Specialized tab for managing MQL5 coding prompts with code editor
 *
 * Features:
 * - Category tabs: Templates, Rules, Patterns
 * - Monaco-style code editor for prompt values
 * - Syntax highlighting for MQL5 code blocks
 * - Quick actions: Copy, Edit, Delete
 * - Add new prompt with template suggestions
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Skeleton,
    Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import BugReportIcon from '@mui/icons-material/BugReport';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';

import {
    getPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    MQL5_CATEGORIES,
    PROMPT_LIMITS,
    PROMPT_KEY_REGEX
} from '../../api/promptsApi';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' },
        mql5: { template: '#22c55e', rules: '#f59e0b', patterns: '#3b82f6' }
    },
    borderRadius: { sm: '8px', md: '12px' },
    transitions: { hover: '200ms cubic-bezier(0.4,0,0.2,1)' }
};

// MQL5 Category configuration
const MQL5_CATEGORY_CONFIG = {
    mql5_template: {
        label: 'Templates',
        icon: <CodeIcon />,
        color: theme.colors.mql5.template,
        description: 'EA and Indicator skeleton templates',
        placeholder: `When user asks to create an EA, use this structure:
\`\`\`mql5
//+------------------------------------------------------------------+
//| Expert Advisor: {EA_NAME}
//+------------------------------------------------------------------+
#property copyright "User"
#property version   "1.00"
#property strict

// Your template code here...
\`\`\``
    },
    mql5_rules: {
        label: 'Rules',
        icon: <BugReportIcon />,
        color: theme.colors.mql5.rules,
        description: 'Bug fix patterns and coding rules',
        placeholder: `When fixing MQL5 bugs, follow these rules:
1. **Array Bounds**: Always check ArraySize() before accessing
2. **Handle Validation**: Check ticket > 0 after OrderSend()
3. **Division by Zero**: Validate denominator != 0
...`
    },
    mql5_patterns: {
        label: 'Patterns',
        icon: <AutoFixHighIcon />,
        color: theme.colors.mql5.patterns,
        description: 'Common code patterns and snippets',
        placeholder: `Standard order management pattern:
\`\`\`mql5
bool OpenBuyOrder(double lots, int sl, int tp, int magic) {
    double price = Ask;
    double stopLoss = sl > 0 ? price - sl * _Point : 0;
    // Pattern code...
}
\`\`\``
    }
};

// Code Editor Component (Monaco-like styling)
const CodeEditor = ({ value, onChange, placeholder, error, readOnly = false, minRows = 12, maxRows = 25 }) => {
    const [lineCount, setLineCount] = useState(1);
    const textareaRef = useRef(null);

    useEffect(() => {
        const lines = (value || '').split('\n').length;
        setLineCount(Math.max(lines, minRows));
    }, [value, minRows]);

    const handleScroll = (e) => {
        const lineNumbers = e.target.previousSibling;
        if (lineNumbers) {
            lineNumbers.scrollTop = e.target.scrollTop;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                border: `1px solid ${error ? theme.colors.status.error : theme.colors.border.tertiary}`,
                borderRadius: theme.borderRadius.sm,
                overflow: 'hidden',
                background: '#1e1e1e', // VS Code dark background
                '&:focus-within': {
                    borderColor: error ? theme.colors.status.error : theme.colors.accent.primary
                }
            }}
        >
            {/* Line Numbers */}
            <Box
                sx={{
                    width: 50,
                    background: '#252526',
                    borderRight: '1px solid #3c3c3c',
                    padding: '12px 0',
                    overflow: 'hidden',
                    userSelect: 'none'
                }}
            >
                {Array.from({ length: Math.min(lineCount, maxRows) }, (_, i) => (
                    <Typography
                        key={i}
                        sx={{
                            color: '#858585',
                            fontSize: '0.8125rem',
                            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                            lineHeight: '1.5',
                            textAlign: 'right',
                            pr: 1,
                            height: '1.5em'
                        }}
                    >
                        {i + 1}
                    </Typography>
                ))}
            </Box>

            {/* Code Area */}
            <Box sx={{ flex: 1, position: 'relative' }}>
                <textarea
                    ref={textareaRef}
                    value={value || ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    onScroll={handleScroll}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    style={{
                        width: '100%',
                        minHeight: `${minRows * 1.5}em`,
                        maxHeight: `${maxRows * 1.5}em`,
                        padding: '12px',
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: '#d4d4d4',
                        fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                        fontSize: '0.8125rem',
                        lineHeight: '1.5',
                        resize: 'vertical',
                        overflow: 'auto'
                    }}
                />
            </Box>
        </Box>
    );
};

// Prompt Card Component
const PromptCard = ({ prompt, onEdit, onDelete, onCopy, expanded, onToggleExpand }) => {
    const config = MQL5_CATEGORY_CONFIG[prompt.category] || MQL5_CATEGORY_CONFIG.mql5_template;
    const codeBlockMatch = prompt.promptText?.match(/```mql5?\n([\s\S]*?)```/);
    const hasCode = !!codeBlockMatch;

    return (
        <Paper
            sx={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: theme.borderRadius.md,
                overflow: 'hidden',
                transition: theme.transitions.hover,
                '&:hover': {
                    borderColor: config.color + '50'
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: expanded ? `1px solid ${theme.colors.border.primary}` : 'none',
                    cursor: 'pointer'
                }}
                onClick={onToggleExpand}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: theme.borderRadius.sm,
                            background: `${config.color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: config.color
                        }}
                    >
                        {config.icon}
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '0.9375rem' }}>
                                {prompt.promptName}
                            </Typography>
                            {hasCode && (
                                <Chip
                                    label="MQL5"
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        background: `${config.color}20`,
                                        color: config.color
                                    }}
                                />
                            )}
                            <Chip
                                label={prompt.isActive ? 'ON' : 'OFF'}
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    background: prompt.isActive ? `${theme.colors.status.success}20` : `${theme.colors.status.error}20`,
                                    color: prompt.isActive ? theme.colors.status.success : theme.colors.status.error
                                }}
                            />
                        </Box>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                            {prompt.promptKey}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Copy">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onCopy(); }}
                            sx={{ color: theme.colors.text.quaternary, '&:hover': { color: theme.colors.status.info } }}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            sx={{ color: theme.colors.text.quaternary, '&:hover': { color: theme.colors.accent.tertiary } }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            sx={{ color: theme.colors.text.quaternary, '&:hover': { color: theme.colors.status.error } }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>

            {/* Expanded Content */}
            <Collapse in={expanded}>
                <Box sx={{ p: 2 }}>
                    {prompt.description && (
                        <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem', mb: 2 }}>
                            {prompt.description}
                        </Typography>
                    )}
                    <CodeEditor
                        value={prompt.promptText}
                        readOnly
                        minRows={6}
                        maxRows={20}
                    />
                </Box>
            </Collapse>
        </Paper>
    );
};

// Edit/Create Dialog Component
const MQL5PromptDialog = ({ open, prompt, isCreating, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        promptKey: '',
        promptName: '',
        promptText: '',
        description: '',
        category: 'mql5_template',
        isActive: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (prompt && !isCreating) {
            setFormData({
                promptKey: prompt.promptKey || '',
                promptName: prompt.promptName || '',
                promptText: prompt.promptText || '',
                description: prompt.description || '',
                category: prompt.category || 'mql5_template',
                isActive: prompt.isActive ?? true
            });
        } else if (isCreating) {
            setFormData({
                promptKey: '',
                promptName: '',
                promptText: '',
                description: '',
                category: 'mql5_template',
                isActive: true
            });
        }
        setError(null);
        setValidationErrors({});
    }, [prompt, isCreating, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const errors = {};
        if (isCreating) {
            if (!formData.promptKey.trim()) {
                errors.promptKey = 'Prompt key is required';
            } else if (!PROMPT_KEY_REGEX.test(formData.promptKey)) {
                errors.promptKey = 'Key must start with letter, contain only lowercase letters, numbers, and underscores';
            }
        }
        if (!formData.promptName.trim()) {
            errors.promptName = 'Display name is required';
        }
        if (!formData.promptText.trim()) {
            errors.promptText = 'Prompt text is required';
        } else if (formData.promptText.length > PROMPT_LIMITS.promptText) {
            errors.promptText = `Text must be ${PROMPT_LIMITS.promptText.toLocaleString()} characters or less`;
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setError(null);

        const payload = {
            promptName: formData.promptName.trim(),
            promptText: formData.promptText,
            description: formData.description.trim(),
            isActive: formData.isActive,
            supportsVariables: false // MQL5 prompts don't use variables
        };

        if (isCreating) {
            payload.promptKey = formData.promptKey.trim();
            payload.category = formData.category;
        }

        const result = isCreating
            ? await createPrompt(payload)
            : await updatePrompt(formData.promptKey, payload);

        setLoading(false);

        if (result.success) {
            toast.success(isCreating ? 'MQL5 prompt created' : 'MQL5 prompt updated');
            onSuccess?.();
        } else {
            setError(result.error);
        }
    };

    const config = MQL5_CATEGORY_CONFIG[formData.category];
    const charCount = formData.promptText.length;
    const charCountColor = charCount > PROMPT_LIMITS.promptText
        ? theme.colors.status.error
        : charCount > PROMPT_LIMITS.promptText * 0.9
            ? theme.colors.status.warning
            : theme.colors.text.quaternary;

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            background: theme.colors.bg.tertiary,
            color: theme.colors.text.primary,
            '& fieldset': { borderColor: theme.colors.border.primary },
            '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
            '&.Mui-focused fieldset': { borderColor: theme.colors.accent.primary }
        },
        '& .MuiInputLabel-root': { color: theme.colors.text.quaternary },
        '& .MuiInputLabel-root.Mui-focused': { color: theme.colors.accent.tertiary }
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.border.primary}`,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.colors.border.primary}`,
                    pb: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: theme.borderRadius.sm,
                            background: `${config?.color || theme.colors.accent.primary}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: config?.color || theme.colors.accent.primary
                        }}
                    >
                        {config?.icon || <CodeIcon />}
                    </Box>
                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '1.25rem' }}>
                        {isCreating ? 'Create MQL5 Prompt' : `Edit: ${prompt?.promptName || ''}`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={loading} sx={{ color: theme.colors.text.quaternary }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, background: `${theme.colors.status.error}20`, color: theme.colors.status.error }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Row 1: Key & Category */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Prompt Key"
                            value={formData.promptKey}
                            onChange={(e) => handleChange('promptKey', e.target.value)}
                            disabled={!isCreating}
                            error={!!validationErrors.promptKey}
                            helperText={validationErrors.promptKey || (isCreating ? 'e.g., ea_skeleton, bug_fix_patterns' : 'Read-only')}
                            fullWidth
                            sx={textFieldSx}
                            inputProps={{ style: { fontFamily: 'monospace' } }}
                        />
                        <FormControl fullWidth sx={textFieldSx}>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                onChange={(e) => handleChange('category', e.target.value)}
                                label="Category"
                                disabled={!isCreating}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                }}
                            >
                                {MQL5_CATEGORIES.map(cat => {
                                    const catConfig = MQL5_CATEGORY_CONFIG[cat];
                                    return (
                                        <MenuItem key={cat} value={cat}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ color: catConfig.color }}>{catConfig.icon}</Box>
                                                {catConfig.label} - {catConfig.description}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Row 2: Display Name */}
                    <TextField
                        label="Display Name"
                        value={formData.promptName}
                        onChange={(e) => handleChange('promptName', e.target.value)}
                        error={!!validationErrors.promptName}
                        helperText={validationErrors.promptName}
                        fullWidth
                        required
                        sx={textFieldSx}
                    />

                    {/* Row 3: Description */}
                    <TextField
                        label="Description"
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        helperText="Brief description of what this prompt does"
                        fullWidth
                        sx={textFieldSx}
                    />

                    {/* Row 4: Code Editor */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                                Prompt Value (MQL5 Code/Rules) *
                            </Typography>
                            <Typography sx={{ color: charCountColor, fontSize: '0.75rem' }}>
                                {charCount.toLocaleString()} / {PROMPT_LIMITS.promptText.toLocaleString()}
                            </Typography>
                        </Box>
                        <CodeEditor
                            value={formData.promptText}
                            onChange={(value) => handleChange('promptText', value)}
                            placeholder={config?.placeholder || 'Enter MQL5 code template or rules...'}
                            error={!!validationErrors.promptText}
                            minRows={15}
                            maxRows={30}
                        />
                        {validationErrors.promptText && (
                            <Typography sx={{ color: theme.colors.status.error, fontSize: '0.75rem', mt: 0.5 }}>
                                {validationErrors.promptText}
                            </Typography>
                        )}
                    </Box>

                    {/* Row 5: Status Toggle */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: theme.colors.status.success },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.colors.status.success }
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ color: theme.colors.text.secondary }}>
                                Active - {formData.isActive ? 'This prompt will be used by Q.AI' : 'This prompt is disabled'}
                            </Typography>
                        }
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2, gap: 1 }}>
                <Button onClick={onClose} disabled={loading} sx={{ color: theme.colors.text.tertiary }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        background: config?.color || theme.colors.accent.primary,
                        '&:hover': { background: config?.color ? `${config.color}cc` : theme.colors.accent.secondary }
                    }}
                >
                    {loading ? 'Saving...' : (isCreating ? 'Create Prompt' : 'Save Changes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main Component
const MQL5PromptsTab = () => {
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('mql5_template');
    const [expandedPrompt, setExpandedPrompt] = useState(null);

    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    // Fetch prompts
    const fetchPrompts = useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await getPrompts();
        if (result.success) {
            // Filter only MQL5 prompts
            const mql5Prompts = result.data.filter(p => MQL5_CATEGORIES.includes(p.category));
            setPrompts(mql5Prompts);
        } else {
            setError(result.error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchPrompts();
    }, [fetchPrompts]);

    // Filter prompts
    const filteredPrompts = prompts.filter(p => {
        const matchesCategory = p.category === activeCategory;
        const matchesSearch = !searchQuery ||
            p.promptName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.promptKey?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.description?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Handlers
    const handleCreate = () => {
        setSelectedPrompt(null);
        setIsCreating(true);
        setDialogOpen(true);
    };

    const handleEdit = (prompt) => {
        setSelectedPrompt(prompt);
        setIsCreating(false);
        setDialogOpen(true);
    };

    const handleDelete = async (prompt) => {
        if (!window.confirm(`Delete "${prompt.promptName}"? This action cannot be undone.`)) return;

        const result = await deletePrompt(prompt.promptKey);
        if (result.success) {
            toast.success('Prompt deleted');
            fetchPrompts();
        } else {
            toast.error(result.error);
        }
    };

    const handleCopy = async (prompt) => {
        try {
            await navigator.clipboard.writeText(prompt.promptText);
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Failed to copy');
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedPrompt(null);
    };

    const handleDialogSuccess = () => {
        handleDialogClose();
        fetchPrompts();
    };

    // Stats
    const getCategoryStats = (category) => prompts.filter(p => p.category === category).length;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.25rem', fontWeight: 700 }}>
                        MQL5 Coding Prompts
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                        Manage EA templates, coding rules, and common patterns for Q.AI code assistance
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchPrompts} disabled={loading} sx={{ color: theme.colors.text.tertiary }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                        sx={{
                            background: theme.colors.accent.primary,
                            '&:hover': { background: theme.colors.accent.secondary }
                        }}
                    >
                        Add Prompt
                    </Button>
                </Box>
            </Box>

            {/* Category Tabs */}
            <Paper
                sx={{
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md,
                    mb: 3
                }}
            >
                <Tabs
                    value={activeCategory}
                    onChange={(_, value) => { setActiveCategory(value); setExpandedPrompt(null); }}
                    sx={{
                        borderBottom: `1px solid ${theme.colors.border.primary}`,
                        '& .MuiTabs-indicator': { backgroundColor: MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary }
                    }}
                >
                    {MQL5_CATEGORIES.map(cat => {
                        const config = MQL5_CATEGORY_CONFIG[cat];
                        const count = getCategoryStats(cat);
                        return (
                            <Tab
                                key={cat}
                                value={cat}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ color: activeCategory === cat ? config.color : theme.colors.text.quaternary }}>
                                            {config.icon}
                                        </Box>
                                        <Typography sx={{ color: activeCategory === cat ? theme.colors.text.primary : theme.colors.text.tertiary }}>
                                            {config.label}
                                        </Typography>
                                        <Chip
                                            label={count}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                background: activeCategory === cat ? `${config.color}30` : theme.colors.bg.quaternary,
                                                color: activeCategory === cat ? config.color : theme.colors.text.quaternary
                                            }}
                                        />
                                    </Box>
                                }
                                sx={{ textTransform: 'none', py: 2 }}
                            />
                        );
                    })}
                </Tabs>

                {/* Search Bar */}
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <TextField
                        placeholder={`Search ${MQL5_CATEGORY_CONFIG[activeCategory]?.label.toLowerCase() || 'prompts'}...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: theme.colors.bg.tertiary,
                                color: theme.colors.text.primary,
                                '& fieldset': { borderColor: theme.colors.border.primary },
                                '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
                                '&.Mui-focused fieldset': { borderColor: MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.colors.text.quaternary }} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                {/* Content */}
                <Box sx={{ p: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, background: `${theme.colors.status.error}20`, color: theme.colors.status.error }}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {[1, 2, 3].map(i => (
                                <Skeleton key={i} variant="rounded" height={80} sx={{ bgcolor: theme.colors.bg.tertiary }} />
                            ))}
                        </Box>
                    ) : filteredPrompts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    background: `${MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                }}
                            >
                                {MQL5_CATEGORY_CONFIG[activeCategory]?.icon || <DescriptionIcon />}
                            </Box>
                            <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, mb: 0.5 }}>
                                {searchQuery ? 'No prompts match your search' : `No ${MQL5_CATEGORY_CONFIG[activeCategory]?.label.toLowerCase() || 'prompts'} yet`}
                            </Typography>
                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem', mb: 2 }}>
                                {searchQuery ? 'Try a different search term' : MQL5_CATEGORY_CONFIG[activeCategory]?.description}
                            </Typography>
                            {!searchQuery && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreate}
                                    sx={{
                                        borderColor: MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary,
                                        color: MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary,
                                        '&:hover': {
                                            borderColor: MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary,
                                            background: `${MQL5_CATEGORY_CONFIG[activeCategory]?.color || theme.colors.accent.primary}15`
                                        }
                                    }}
                                >
                                    Create First {MQL5_CATEGORY_CONFIG[activeCategory]?.label || 'Prompt'}
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {filteredPrompts.map(prompt => (
                                <PromptCard
                                    key={prompt.promptKey}
                                    prompt={prompt}
                                    expanded={expandedPrompt === prompt.promptKey}
                                    onToggleExpand={() => setExpandedPrompt(expandedPrompt === prompt.promptKey ? null : prompt.promptKey)}
                                    onEdit={() => handleEdit(prompt)}
                                    onDelete={() => handleDelete(prompt)}
                                    onCopy={() => handleCopy(prompt)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Edit/Create Dialog */}
            <MQL5PromptDialog
                open={dialogOpen}
                prompt={selectedPrompt}
                isCreating={isCreating}
                onClose={handleDialogClose}
                onSuccess={handleDialogSuccess}
            />
        </Box>
    );
};

export default MQL5PromptsTab;
